import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const Header = () => {
  const { user, role, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [passwords, setPasswords] = useState({ new: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  
  const isAdmin = role === 'SUPER_ADMIN' || role === 'ADMIN';
  const displayName = user?.name || (isAdmin ? 'Admin' : 'User');
  const displayRole = isAdmin ? 'Centre Administrator' : role === 'ACARYA' ? 'Acarya Consultant' : (user?.type || 'User');
  const avatarUrl = user?.image || `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=1B1036&color=F6DC96&bold=true`;

  const [isSessionsModalOpen, setIsSessionsModalOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  
  // Extract SID from JWT
  const currentSid = (() => {
    try {
      const token = localStorage.getItem('amps_token');
      if (!token) return null;
      return JSON.parse(atob(token.split('.')[1])).sid;
    } catch {
      return null;
    }
  })();

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await api.get('/auth/me');
      if (res.data.success && res.data.user.activeSessions) {
        setSessions(res.data.user.activeSessions);
      }
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleOpenSessions = () => {
    setIsDropdownOpen(false);
    setIsSessionsModalOpen(true);
    fetchSessions();
  };

  const handleRevokeSession = async (sid) => {
    if (!window.confirm("Are you sure you want to revoke this session?")) return;
    try {
      const res = await api.delete(`/auth/sessions/${sid}`);
      if (res.data.success) {
        setSessions(prev => prev.filter(s => s.sid !== sid));
        alert("✅ Device logged out successfully!");
      }
    } catch (error) {
      alert("❌ Failed to revoke session: " + (error.response?.data?.message || error.message));
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) return alert("Passwords do not match");
    if (passwords.new.length < 6) return alert("Password must be at least 6 characters");

    setLoading(true);
    try {
      const res = await api.put('/auth/update-password', { password: passwords.new });
      if (res.data.success) {
        alert("✅ Password updated successfully!");
        setIsModalOpen(false);
        setPasswords({ new: '', confirm: '' });
      }
    } catch (error) {
      alert("❌ Failed to update password: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <header className="bg-[#0F0D0C] sticky top-0 px-8 py-4 z-30 font-serif flex items-center justify-between border-b border-white/5">
      {/* Left: Greeting */}
      <div>
        <h2 className="text-lg font-bold text-white">Baba Nam Kevalam 🙏</h2>
        <p className="text-[10px] text-stone-500 mt-0.5">Welcome back, {displayName}</p>
      </div>

      {/* Right: Centre Selector + Notifications + Profile */}
      <div className="flex items-center gap-5">
        {/* Profile */}
        <div className="relative">
          <div 
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-3 cursor-pointer group"
          >
            <img src={avatarUrl} alt={displayName} className="w-9 h-9 rounded-full border border-white/10 group-hover:border-[#F0C75E]/50 transition-all object-cover" />
            <div className="hidden sm:block text-right">
              <div className="text-xs font-bold text-white group-hover:text-[#F0C75E] transition-colors">{displayName}</div>
              <div className="text-[9px] text-stone-500">{displayRole}</div>
            </div>
            <span className={`material-symbols-outlined text-stone-600 text-sm transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}>expand_more</span>
          </div>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-3 w-56 bg-[#0E0E26] border border-white/10 rounded-2xl shadow-2xl py-2 z-50">
              <div className="px-4 py-3 border-b border-white/5 mb-2">
                <div className="text-xs font-bold text-white">{displayName}</div>
                <div className="text-[9px] text-stone-500 mt-1">{user?.email}</div>
              </div>
              <button 
                onClick={() => { setIsModalOpen(true); setIsDropdownOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-[11px] text-stone-400 hover:bg-white/5 hover:text-[#F0C75E] flex items-center gap-3 transition-all"
              >
                <span className="material-symbols-outlined text-sm">lock_reset</span>
                Change Password
              </button>
              <button 
                onClick={handleOpenSessions}
                className="w-full text-left px-4 py-2.5 text-[11px] text-stone-400 hover:bg-white/5 hover:text-[#F0C75E] flex items-center gap-3 transition-all"
              >
                <span className="material-symbols-outlined text-sm">devices</span>
                Active Sessions
              </button>
              <div className="h-px bg-white/5 my-1"></div>
              <button 
                onClick={() => { logout(); setIsDropdownOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-[11px] text-red-500 hover:bg-red-500/10 flex items-center gap-3 transition-all"
              >
                <span className="material-symbols-outlined text-sm">logout</span>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-[#141210] border border-white/10 rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="border-b border-white/5 px-8 py-5 flex justify-between items-center">
              <h3 className="font-serif text-lg font-bold text-[#F0C75E]">Update Password</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-stone-500 hover:text-white transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <form onSubmit={handleUpdatePassword} className="p-8 space-y-5">
              <div>
                <label className="block text-[10px] font-black text-stone-500 uppercase tracking-widest mb-2">New Password</label>
                <input 
                  type="password"
                  required
                  value={passwords.new}
                  onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                  className="w-full bg-[#0E0E26] border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#F0C75E]/30"
                  placeholder="Min 6 characters"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-stone-500 uppercase tracking-widest mb-2">Confirm Password</label>
                <input 
                  type="password"
                  required
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                  className="w-full bg-[#0E0E26] border border-white/5 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#F0C75E]/30"
                  placeholder="Repeat new password"
                />
              </div>
              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#F0C75E] text-black font-black uppercase tracking-widest py-3.5 rounded-xl hover:bg-white transition-all shadow-xl disabled:opacity-50 mt-4"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Active Sessions Modal */}
      {isSessionsModalOpen && (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center backdrop-blur-sm p-4">
          <div className="bg-[#141210] border border-white/10 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="border-b border-white/5 px-8 py-5 flex justify-between items-center">
              <div>
                <h3 className="font-serif text-lg font-bold text-[#F0C75E]">Active Sessions</h3>
                <p className="text-[10px] text-stone-400 mt-0.5">Manage your logged-in devices</p>
              </div>
              <button onClick={() => setIsSessionsModalOpen(false)} className="text-stone-500 hover:text-white transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-8 max-h-[60vh] overflow-y-auto space-y-4">
              {loadingSessions ? (
                <div className="text-center text-stone-500 py-8 text-sm">Loading sessions...</div>
              ) : sessions.length === 0 ? (
                <div className="text-center text-stone-500 py-8 text-sm">No active sessions found.</div>
              ) : (
                sessions.map((session) => (
                  <div key={session.sid} className="bg-[#0E0E26] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-bold text-white flex items-center gap-2">
                        {session.deviceInfo}
                        {session.sid === currentSid && (
                          <span className="text-[9px] bg-[#F0C75E]/20 text-[#F0C75E] px-2 py-0.5 rounded-full border border-[#F0C75E]/30">Current Device</span>
                        )}
                      </div>
                      <div className="text-[10px] text-stone-500 mt-1">
                        Last active: {new Date(session.lastActive).toLocaleString()}
                      </div>
                    </div>
                    {session.sid !== currentSid && (
                      <button 
                        onClick={() => handleRevokeSession(session.sid)}
                        className="text-[10px] font-bold text-red-400 hover:text-red-300 bg-red-400/10 hover:bg-red-400/20 px-3 py-1.5 rounded-lg transition-colors border border-red-400/20"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;